/* eslint-disable no-return-assign */

import React, { Component } from 'react';

import { checkSizeChange } from '@nicholasmole/check-size-change';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import ReactToPrint from 'react-to-print';
import { bindActionCreators } from 'redux';

import {
    storeActions,
    userInfoSelectors,
    redirect_records as redirectRecords,
} from '@src/ducks';
import {
    setSandwichMenu,
    setAppHistory,
    setUserInfo,
    resetRedirectRecords,
    fetchRedirectRecords,
} from '@src/requests';
import googleAnalytics from '@src/services/googleAnalytics';
import gtm from '@src/services/gtm';
import hotJar from '@src/services/hotjar';
import { isImpersonate } from '@src/utils/jwtHelpers';
import { parseSearch, stringifySearch } from '@src/utils/urlHelpers';

import { BreadCrumbProvider, BreadCrumbs } from '@src/components/breadcrumbs';
import ScrollToTop from '@src/components/common/ScrollToTop';
import {
    Footer,
} from '@src/components/footer';
import {
    PrinterAreaContainer,
} from '@src/components/printer';
import { RedirectRecords } from '@src/components/redirectRecords';

import {
    LoadApp,
} from './errors';
import {
    Header,
} from './header';
import { HelpBanner } from './HelpBanner';
import { InnerpagePromoBanner } from './InnerpagePromoBanner';
import { SessionInactivityCheck } from './sessionInactivityCheck';
import { unique } from '../utils/componentHelpers';

import ml from '@src/css/metlife_css/buttons.scss';

import styles from './app.scss';
import './print-scss.scss';

const mapStateToProps = (state) => ({
    exp: userInfoSelectors.getExp(state),
    redirectRecordsData: redirectRecords.getRedirectRecords(state),
    ...state,
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({
        ...storeActions,
    }, dispatch),
});

/**
 * #App
 *
 * Main Application
 *
 * render returns
 * | LoadApp - makes sure pages are loaded without error
 * 		|- Header - Navigation and Header Menu
 * 		|- PrinterAreaContainer - Area that is printed using Print Component
 * 				|-- BreadCrumbs && Printer Button - Top portion of page visible on all pages besides Home Page
 * 				|-- renderRoutes - Create all page contents generated from URL
 * 		|- Footer - Footer Content
 *
 * @param {Component} LoadApp - Check there is no 400/500 Error in the redux request
 * @param {Component} TopHeader - Logo, Links, Alerts, Help, and Logout area
 * @param {Component} TopMenu - Navigation area - appears below header when app is larger than tablet size.
 * @param {Component} PrinterAreaContainer - Wrapper area for application that is printable.
 * @param {function} renderRoutes - react-router-config routes location in src/routes
 * @param {function} toggledClick - Drop down for menu area
 * @param {function} collectPrintRedirect - redirect app back to current page to correct Printer Button that
 * tries to bring to home page on print.
 */

class App extends Component {
    constructor(props) {
        super(props);
        this.fetch = unique();
        this.toggledClick = this.toggledClick.bind(this);
    }

    static propTypes = {
        actions: PropTypes.objectOf(PropTypes.func).isRequired,
        isLimit: PropTypes.bool,
        route: PropTypes.object.isRequired,
        redirectRecordsData: ImmutablePropTypes.mapContains({
            loading: PropTypes.string,
            // eslint-disable-next-line camelcase
            redirect_records: ImmutablePropTypes.list,
        }),
    };

    /**
	 * ### toggledClick
	 * Click the mobile header menu
	 */

    toggledClick = () => {
        const {
            actions,
            route,
        } = this.props;

        actions.appRequest(setSandwichMenu('clicked'));
        actions.appRequest(setAppHistory(route.history));
    };

    componentDidMount() {
        const {
            actions,
            route,
        } = this.props;

        actions.appRequest(setAppHistory(route.history));
        actions.appRequest(setUserInfo());
        this.loadRedirectRecords();
        googleAnalytics(route.history);
        gtm(route.history);
        hotJar();
    }

    componentDidUpdate(prevProps) {
        const { route: { location: { pathname } } } = this.props;
        const { route: { location: { pathname: prevPathname } } } = prevProps;

        if (pathname !== prevPathname) {
            this.loadRedirectRecords();
        }
    }

    collectPrintRedirect() {
        const { route } = this.props;

        route.history.goBack();
    }

    loadRedirectRecords = () => {
        const { route, actions: { appRequest } } = this.props;

        const params = parseSearch(route.history.location.search);
        params.pathname = route.history.location.pathname;
        const search = stringifySearch(params);

        appRequest(resetRedirectRecords());
        appRequest(fetchRedirectRecords(this.fetch, `?${search}`));
    };

    /**
	 * ### printComponent
	 * Content for print area
	 */
    printComponent = () => (
        <a className={`${ml['ml-link']} ${styles['the-print-button']}`} href="#" aria-label="Print">
            <span />
            <div className="the-print-button_text">
                Print
            </div>
        </a>
    );

    breadCrumbLine = () => {
        return (
            <div className={`col ${styles['the-print-button-container']}`}>
                <BreadCrumbs key="bread_crumbs" />
                {
                    /*
                        *	### ReactToPrint
                        *	Needs to on the main app container.
                        *	PrintAreaContainer: contains area that is printable.
                        *	ReactToPrint: Printer button
                        */
                }
                <ReactToPrint
                    closeAfterPrint
                    trigger={() => this.printComponent()}
                    content={() => this.componentRef}
                    onBeforePrint={(props) => this.collectPrintRedirect(props)}
                />
            </div>
        );
    };

    render() {
        const {
            isLimit,
            route,
            redirectRecordsData,
        } = this.props;

        const pathname = route.history.location.pathname;
        const search = route.history.location.search;

        const isHomepage = route.history.location.pathname === '/';
        const isSearchpage = route.history.location.pathname === '/search';
        const isContactpage = route.history.location.pathname === '/contact-us';
        const isProductspage = route.history.location.pathname === '/products';

        const isFullScreenRow = isHomepage || isContactpage || isProductspage;
        const classes = classNames('mx-auto', { 'no-gutters': isHomepage });
        const impersonateClass = isImpersonate() ? 'impersonated' : '';

        return (
            <LoadApp>
                <SessionInactivityCheck>
                    <RedirectRecords pathname={pathname} search={search} redirectRecordsData={redirectRecordsData}>
                        <Container fluid id={styles.app} className="app p-0" >
                            <BreadCrumbProvider>
                                <a className="skip-link" href="#main_content" tabIndex={1000}>Skip to content</a>
                                <ScrollToTop />
                                <Header
                                    key="Header"
                                    toggledClick={this.toggledClick}
                                    isLimit={isLimit}
                                    route={route}
                                    history={route.history}
                                />
                                <PrinterAreaContainer ref={(el) => (this.componentRef = el)} key="PrinterAreaContainer">
                                    <main className={`row no-gutters app-main-content-container ${impersonateClass}`}>
                                        <Container fluid className="main-container pl-0 pr-0">
                                            <Row className="main-container-row">
                                                {!isHomepage && this.breadCrumbLine()}
                                            </Row>
                                            <Row className="main-container-row">
                                                {!isHomepage && <InnerpagePromoBanner />}
                                            </Row>
                                            <Row id={isHomepage ? '' : 'main_content'} className={isFullScreenRow ? classes : 'main-container-row'}>
                                                {
                                                    /**
                                                     * **App Pages Start here**
                                                     * Go to the /src/routes.js to view all
                                                     * possible routes the application.
                                                     */
                                                    renderRoutes(route.routes, { ...this.props })
                                                }
                                            </Row>
                                            <Row className="main-container-row py-5">
                                                {!isHomepage && !isSearchpage && <HelpBanner />}
                                            </Row>
                                        </Container>
                                    </main>
                                </PrinterAreaContainer>
                                <Footer />
                            </BreadCrumbProvider>
                        </Container>
                    </RedirectRecords>
                </SessionInactivityCheck>
            </LoadApp>
        );
    }
}
const extraSizes = [
    {
        prop: 'isLimit',
        limit: 1030,
    },
];

export default connect(mapStateToProps, mapDispatchToProps)(checkSizeChange(App, { extraSizes }));
